import { Table, TableContainer, TableCell, NoRowsOverlay } from '@shared/components';
import { TableBody, TableHeader } from '@shared/table-generator';
import { useMemo } from 'react';

import { type RegistrationType } from '../registration-type-label';

import { getColumns } from './columns';
import { type ExamEventCourseStatus, type ExamEventStatus, type ExamType } from './types';
import { createDayRows } from './utils';

export interface ExamDistributionRoundTime {
  from: string;
  to: string;
}

export interface ExamDistributionRoundInfo<T extends readonly string[]> {
  registrationType: RegistrationType;
  examEventStatus: ExamEventStatus;
  examEventId: string;
  examType: ExamType;
  lastDownloadedAt: Date | undefined;
  examCenterCode: string;
  examCenterId: number;
  location: string;
  roundNumber: number;
  examDate: Date;
  canUpdate: boolean;
  examEventCourses: {
    course: T[number];
    qSetCode: string | undefined;
    status: ExamEventCourseStatus | undefined;
  }[];
}

export interface ExamDistributionRound<T extends readonly string[]> {
  time: ExamDistributionRoundTime;
  info: ExamDistributionRoundInfo<T>[];
}

export interface ExamDistributionDataRowItem<T extends readonly string[]> {
  day: string;
  date: string;
  rounds: ExamDistributionRound<T>[];
}

export interface BaseMonthlyExamDistributionTableProps<T extends readonly string[]> {
  data: ExamDistributionDataRowItem<T>[];
  courses: T;
}

export default function BaseMonthlyExamDistributionTable<T extends readonly string[]>(
  props: Readonly<BaseMonthlyExamDistributionTableProps<T>>,
) {
  const { data, courses } = props;

  const columns = useMemo(() => {
    return getColumns(courses);
  }, [courses]);

  const rows = useMemo(() => {
    return data.flatMap((dayData, index) => {
      const isLastRow = index === data.length - 1;
      return createDayRows(dayData, courses, isLastRow);
    });
  }, [data, courses]);

  return (
    <TableContainer
      sx={(theme) => ({
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: theme.shape.rounded(2),
      })}
    >
      <Table>
        <TableHeader
          columns={columns}
          slotProps={{
            tableHeaderRow: {
              sx: (theme) => ({
                '& > th': {
                  borderRight: `1px solid ${theme.palette.grey[300]}`,
                  borderBottom: `1px solid ${theme.palette.grey[300]}`,
                },
                ':first-of-type': {
                  'th:last-of-type': {
                    borderRight: 'none',
                  },
                },
              }),
            },
            tableHeaderCell: {
              sx: (theme) => ({
                ...theme.typography.body1Semibold,
                color: theme.palette.common.white,
                backgroundColor: theme.palette.grey[800],
                p: theme.spacing(2),
              }),
            },
          }}
        />
        {rows.length === 0 ? (
          <TableCell
            colSpan={columns[0].children.length}
            sx={(theme) => ({ py: theme.spacing(4), width: '100%' })}
          >
            <NoRowsOverlay title='ไม่พบข้อมูลที่ตรงกับการค้นหา' />
          </TableCell>
        ) : (
          <TableBody
            rows={rows}
            slotProps={{
              tableBodyCell: {
                sx: (theme) => ({
                  ...theme.typography.body1,
                  borderRight: `1px solid ${theme.palette.grey[300]}`,
                }),
              },
            }}
          />
        )}
      </Table>
    </TableContainer>
  );
}
